/* eslint-disable comma-spacing */
import React, { useCallback, cloneElement, Children, useState, useContext, useEffect } from 'react'
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import {
  SUBMIT_BUTTON_NAME,
  SOCIAL_FIELD_TITLE,
  DO_YOU_HAVE_ACCOUNT,
  LOGON_BUTTON,
  SERVER_ERROR_MESSAGE_CODE,
  SERVER_ERROR_MESSAGE_HEADER,
  SERVER_ERROR_MESSAGE_DESCRIPTION,
  PHONE_ALREADY_EXISTS_TEXT,
} from '../../../../formComponents/constants'
import { REGISTRATION_TITLE } from '../../../constants'
import IFormRegistrationValues from '../../../IFormRegistrationValues'
import { useValidator } from '../../../../../validator/useValidator'
import { getAppId } from '../../../../../utils/appId'
import { getIsShowSocialAuth, getLkUrl } from '../../../../../utils/url'
import { Captcha } from '../Captcha/Captcha'
import { IError } from '../../../../../interfaces/common'
import { SocialNetworks } from '../../../../../emun/social'
import { useEnv } from '../../../../../utils/useEnv'
import { isRegWithoutPassCase } from '../../../../../utils/registrationPasswordDisplay'
import { FormTitle } from '../../../../FormTitle/FormTitle'
import { ErrorMessageWiget } from '../../../../ErrorMessageWiget/ErrorMessageWiget'
import { Button2 } from '../../../../Button2/Button2'
import { SocialNetworksAuthButtons } from '../../../../SocialNetworksAuthButtons/SocialNetworksAuthButtons'
import { RegistrationFormUserAgreements } from '../../RegistrationFormUserAgreements'
import { Divider } from '../../../../Divider/Divider'
import { ActionButton } from '../../../../ActionButton/ActionButton'
import { ROUTES } from '../../../../../routes/routes'
import style from './RegistrationForm.module.css'
import { LanguageContext } from '../../../../LanguageContext/LanguageContext'
import { getQueryParam } from '../../../../../utils/getQueryParam'
import { useIsCaptchaNeeded } from '../../../../../api/mutations/useIsCaptchaNeeded'
import { useUserAdd } from '../../../../../api/mutations/useUserAdd'
import { IUserChangeParams } from '../../../../../interfaces/IUserChangeParams'
import { sendGATagEvent } from '../../../../../utils/sendGATagEvent'
import { FormFieldTypes } from '../../../../../emun/RegistrationFormFieldTypes'

type RegistrationFormProps<TInitialFormData = any> = {
  universityUserAgreements?: boolean
  onSocialAuth(socialNetwork: SocialNetworks, callbackUrl?: string): void
  initialFormData: TInitialFormData
  onSubmit(values: IFormRegistrationValues): IUserChangeParams
  onSubmitError?(formProps: FormikHelpers<IFormRegistrationValues>, error: any): void
  children: any
  isPhoneConfirmed?: boolean
}

export const RegistrationForm = <TInitialFormData extends IFormRegistrationValues>({
  onSocialAuth,
  children,
  initialFormData,
  onSubmit,
  onSubmitError,
  universityUserAgreements,
  isPhoneConfirmed,
}: RegistrationFormProps<TInitialFormData>) => {
  const [isCaptchaBlock, isCaptchaBlockChange] = useState(false)
  const [isCaptchaSucceed, isCaptchaSucceedChange] = useState(false)
  const deployEnv = useEnv()
  const [serverErrorMessage, serverErrorMessageChange] = useState<boolean>(false)
  const isCaptchaNeeded = useIsCaptchaNeeded()
  const userAdd = useUserAdd()
  const queryParams = useSearchParams()
  const t = useTranslations()
  const { language } = useContext(LanguageContext)
  const { query } = useRouter()
  const callbackUrl = getQueryParam(query, 'callbackurl') || getQueryParam(query, 'callbackUrl') || ''
  const returnUrl = getQueryParam(query, 'returnurl')
  const form = getQueryParam(query, 'form')
  const utmSource = getQueryParam(query, 'utm_source')
  const utmCampaign = getQueryParam(query, 'utm_campaign')
  const utmMedium = getQueryParam(query, 'utm_medium')
  const utmTerm = getQueryParam(query, 'utm_term')
  const utmContent = getQueryParam(query, 'utm_content')
  const lang = getQueryParam(query, 'lang')

  const showSocialAuth = getIsShowSocialAuth(queryParams)

  const { validate } = useValidator<TInitialFormData>(initialFormData)
  const title = t(REGISTRATION_TITLE)
  const submitButtonName = t(SUBMIT_BUTTON_NAME)
  const socialFieldTitle = t(SOCIAL_FIELD_TITLE)
  const doYouHaveAccount = t(DO_YOU_HAVE_ACCOUNT)
  const logonButton = t(LOGON_BUTTON)
  const serverErrorMessageHeader = t(SERVER_ERROR_MESSAGE_HEADER)
  const serverErrorMessageDescription = t(SERVER_ERROR_MESSAGE_DESCRIPTION)

  const onCaptchaSuccess = useCallback(() => {
    isCaptchaSucceedChange(true)
  }, [])

  const onSubmitHandler = async (
    values: IFormRegistrationValues,
    formProps: FormikHelpers<IFormRegistrationValues>,
  ) => {
    try {
      if (!isCaptchaSucceed) {
        const { captchaFlag } = await isCaptchaNeeded()

        if (captchaFlag) {
          isCaptchaBlockChange(true)

          return
        }
      }
      // eslint-disable-next-line no-empty, prettier/prettier
    } catch (e) {}

    try {
      const data = await userAdd({
        ...onSubmit(values),
        appId: getAppId(query),
        form: form ? Number(form) : undefined,
        returnUrl,
        utmSource,
        utmCampaign,
        utmMedium,
        utmTerm,
        utmContent,
        urlreferral: window.location.href,
        lang,
        ...(callbackUrl && { callbackUrl }),
      })

      sendGATagEvent('event', 'EmailSent', {
        event_category: 'Registration',
        event_label: window.location.href,
      })

      if (data.callbackUrl) {
        window.location.href = data.callbackUrl
      }
    } catch (error) {
      if ((error as IError).response.status === SERVER_ERROR_MESSAGE_CODE) {
        serverErrorMessageChange(true)
      } else if (onSubmitError) {
        onSubmitError(formProps, error)

        serverErrorMessageChange(false)
      } else {
        serverErrorMessageChange(false)
      }
    }
  }

  const displayPassword = !(callbackUrl && isRegWithoutPassCase(callbackUrl))

  const childrenToShow = displayPassword
    ? children
    : (children.filter((obj: Record<string, any>) => obj.type.displayName !== 'FormPasswordField') as any)

  // переписать
  const loginLink = () => {
    let res = `${getLkUrl(ROUTES.LOGON_PAGE, deployEnv, language)}?lang=${language}`
    const appId = getAppId(query)

    if (appId) res += `&appid=${appId}`
    if (!showSocialAuth) res += '&hide_social_buttons=1'

    return res
  }

  const sendGATagEvents = async (values: IFormRegistrationValues, errors: FormikErrors<IFormRegistrationValues>) => {
    const emailError = await validate('email')(values.email)
    const firstNameError = await validate('firstName')(values.firstName)
    const lastNameError = await validate('lastName')(values.lastName)
    const phoneError = await validate('phone')(values.phone)
    const positionError = await validate('position')(values.position || '')

    if (FormFieldTypes.Email in initialFormData && !emailError) {
      sendGATagEvent('event', 'EmailOK', {
        event_category: 'Registration',
        event_label: window.location.href,
      })
    }

    if (FormFieldTypes.FirstName in initialFormData && firstNameError) {
      sendGATagEvent('event', 'ErrName', {
        event_category: 'Registration',
        event_label: window.location.href,
      })
    }

    if (FormFieldTypes.LastName in initialFormData && lastNameError) {
      sendGATagEvent('event', 'ErrSurname', {
        event_category: 'Registration',
        event_label: window.location.href,
      })
    }

    if (FormFieldTypes.Phone in initialFormData && phoneError) {
      sendGATagEvent('event', 'ErrPhone', {
        event_category: 'Registration',
        event_label: window.location.href,
      })
    }

    if (FormFieldTypes.Position in initialFormData && positionError) {
      sendGATagEvent('event', 'ErrPost', {
        event_category: 'Registration',
        event_label: window.location.href,
      })
    }

    if (FormFieldTypes.Phone in initialFormData && errors.phone === PHONE_ALREADY_EXISTS_TEXT) {
      sendGATagEvent('event', 'PhoneExist', {
        event_category: 'Registration',
        event_label: window.location.href,
      })
    }
  }

  useEffect(() => {
    sendGATagEvent('event', 'Start', {
      event_category: 'Registration',
      event_label: window.location.href,
    })
  }, [])

  return (
    <Formik<IFormRegistrationValues>
      initialValues={initialFormData}
      onSubmit={onSubmitHandler}
      validateOnBlur
      validateOnChange={false}
    >
      {({ handleSubmit, values, errors }) => {
        const childrenWithProps = Children.map(childrenToShow, (child) =>
          cloneElement(child, {
            validate,
            formQueryParam: form,
            appidQueryParam: getAppId(query),
          }),
        )

        return (
          <Form>
            <FormTitle text={title} />
            {childrenWithProps}
            {isCaptchaBlock && <Captcha onSuccess={onCaptchaSuccess} />}
            {serverErrorMessage && (
              <ErrorMessageWiget header={serverErrorMessageHeader} description={serverErrorMessageDescription} />
            )}
            <Button2
              data-qa-locator="submit"
              className={style.submitButton}
              onMouseDown={() => {
                sendGATagEvents(values, errors)
                handleSubmit()
              }}
              isDisabled={isPhoneConfirmed || (isCaptchaBlock && !isCaptchaSucceed)}
            >
              {submitButtonName}
            </Button2>
            {showSocialAuth && (
              <div className={style.socialBlock}>
                <div className={style.fieldHeader}>{socialFieldTitle}</div>
                <SocialNetworksAuthButtons onSocialAuth={onSocialAuth} language={language} />
              </div>
            )}
            <RegistrationFormUserAgreements universityUserAgreements={universityUserAgreements} />
            <Divider className={style.divider} />
            <div>
              <div className={style.fieldHeader}>{doYouHaveAccount}</div>
              <ActionButton data-qa-locator="create-account" link={loginLink()} className={style.logonButton}>
                {logonButton}
              </ActionButton>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

RegistrationForm.displayName = 'RegistrationForm'
